import { IStep, IStepFields } from "../../interface/MedicalRecord";

export const buildDefaultValues = (template: any) => {
  const defaults: any = {};

  template?.Steps?.forEach((step: any) => {
    const stepKey = step?.Id.toString();
    defaults[`id_${stepKey}`] = { IsVisible: step?.IsVisible };

    // StepFields
    if (step?.StepFields?.length) {
      defaults[`id_${stepKey}`].StepFields = {};
      step?.StepFields?.forEach((field: any) => {
        defaults[`id_${stepKey}`].StepFields[`field_${field?.Id}`] = {
          IsVisible: field?.IsVisible,
          ColumnSize: field?.ColumnSize,
        };
      });
    }

    // InverseParentSteps
    if (step?.InverseParentSteps?.length) {
      defaults[`id_${stepKey}`].InverseParentSteps = {};
      step.InverseParentSteps.forEach((sub: any) => {
        const subKey = `id_${sub.Id}`;
        defaults[`id_${stepKey}`].InverseParentSteps[subKey] = {
          StepFields: {},
          IsVisible: sub?.IsVisible,
          ColumnSize: sub?.ColumnSize,
        };

        sub?.StepFields?.forEach((field: any) => {
          defaults[`id_${stepKey}`].InverseParentSteps[subKey].StepFields[
            `field_${field?.Id}`
          ] = {
            IsVisible: field?.IsVisible,
            ColumnSize: sub?.ColumnSize,
          };
        });
      });
    }
  });
  return defaults;
};

export const logFieldOrder = (steps: any[]) => {
  const fieldOrder: Array<{ id: string; name: string; order: number }> = [];

  steps.forEach((step, stepIndex) => {
    step.StepFields?.forEach((field: any, fieldIndex: number) => {
      fieldOrder.push({
        id: field?.Id,
        name: field?.Name,
        order: fieldIndex + 1,
      });
    });

    step.InverseParentSteps?.forEach((subStep: any, subStepIndex: number) => {
      subStep.StepFields?.forEach((field: any, fieldIndex: number) => {
        fieldOrder.push({
          id: field?.Id,
          name: field?.Name,
          order: fieldIndex + 1,
        });
      });
    });
  });

  return fieldOrder;
};

const appendFormData = (
  formData: FormData,
  data: any,
  parentKey?: string,
) => {
  if (data === null || data === undefined) {
    return;
  }

  if (Array.isArray(data)) {
    data.forEach((item, index) => {
      const key = parentKey
        ? `${parentKey}[${index}]`
        : `${index}`;

      if (
        typeof item === "object" &&
        item !== null &&
        !Array.isArray(item)
      ) {
        appendFormData(formData, item, key);
      } else {
        appendFormData(formData, item, key);
      }
    });

    return;
  }

  if (typeof data === "object" && !(data instanceof File)) {
    Object.entries(data).forEach(([key, value]) => {
      const currentKey = parentKey
        ? `${parentKey}[${key}]`
        : key;

      appendFormData(formData, value, currentKey);
    });

    return;
  }

  formData.append(parentKey ?? "", String(data));
};

export function filterBodyData(
  bodyData: Record<string, any>,
  allowedPatterns: RegExp[],
) {
  const formData = new FormData();

  // Recursively walk and append only allowed keys
  function walkAndAppend(obj: any, path = "") {
    if (Array.isArray(obj)) {
      obj.forEach((item, i) => walkAndAppend(item, `${path}[${i}]`));
    } else if (obj && typeof obj === "object" && !(obj instanceof Blob)) {
      Object.entries(obj).forEach(([key, val]) => {
        const newPath = path ? `${path}.${key}` : key;

        // Check if this key is allowed
        const isAllowed = allowedPatterns.some((pattern) =>
          pattern.test(newPath),
        );

        // If value is primitive and allowed → append
        if (typeof val !== "object" || val instanceof Blob) {
          if (isAllowed && val !== "" && val !== null && val !== undefined) {
            formData.append(newPath, val as any);
          }
        } else {
          // If object/array, keep walking deeper
          walkAndAppend(val, newPath);
        }
      });
    }
  }

  walkAndAppend(bodyData);
  return formData;
}


export const updatedSteps = (localSteps: any[], values: any) =>
  localSteps.map((step: any, index: number) => {
    const fieldOrder = logFieldOrder(localSteps);

    const fieldOrderMap = new Map();
    fieldOrder.forEach((item) => {
      fieldOrderMap.set(item.id, item.order);
    });

    const stepValues = values?.[`id_${step.Id}`] || {};
    // Update substeps with order
    const updatedInverse =
      step.InverseParentSteps?.map((sub: any) => {
        const subValues =
          stepValues?.InverseParentSteps?.[`id_${sub.Id}`]?.StepFields || {};
        return {
          ...sub,
          Id: sub?.Id,
          Name: sub?.Name,
          IsVisible:
            stepValues.InverseParentSteps?.[`id_${sub.Id}`]?.IsVisible ??
            sub.IsVisible,
          StepFields: sub?.StepFields?.map((field: any) => ({
            ...field,
            Id: field?.Id,
            Name: field?.Name,
            IsVisible:
              subValues[`field_${field?.Id}`]?.IsVisible ?? field?.IsVisible,
            Order: fieldOrderMap.get(field?.Id) || field?.Order,
          })),
        };
      }) ?? undefined;

    return {
      Id: step?.Id,
      Name: step?.Name,
      Order: index + 1,
      IsVisible: values?.[`id_${step.Id}`]?.IsVisible ?? step.IsVisible,
      StepFields: step?.StepFields?.map((field: any) => ({
        ...field,
        IsVisible:
          stepValues?.StepFields?.[`field_${field?.Id}`]?.IsVisible ??
          field?.IsVisible,
        Order: fieldOrderMap.get(field?.Id) || field?.Order,
      })),
      InverseParentSteps: updatedInverse,
    };
  });

export const LoadingFieldsSkeleton = (
  <>
    {new Array(5).fill(0).map((_, index: number) => (
      <div
        key={index}
        className="bg-primary-bg col-span-12 mb-2 h-10 w-full animate-pulse rounded-md"
      />
    ))}
  </>
);


export const stringifyValues = (values: any) => {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      if (Array.isArray(value)) {
        return [
          key,
          value.map((item) => {
            if (item && typeof item === "object") {
              return Object.fromEntries(
                Object.entries(item).map(([childKey, childValue]) => [
                  childKey,
                  childValue !== null && childValue !== undefined
                    ? `${childValue}`
                    : childValue,
                ]),
              );
            }

            return item !== null && item !== undefined
              ? `${item}`
              : item;
          }),
        ];
      }

      if (value !== null && typeof value === "object") {
        return [key, JSON.stringify(value)];
      }

      return [
        key,
        value !== null && value !== undefined ? `${value}` : value,
      ];
    }),
  );
};

export const formattedMedicalInputValue = (value: any, t: any) => {
  return value === null || value === undefined
    ? "---"
    : value === true
      ? t("yes")
      : value === false
        ? t("no")
        : t(value);
};

export function flattenFields(
  fields: IStepFields[] = [],
  parentKey?: string,
): (IStepFields & { __parentKey?: string })[] {
  return fields?.flatMap((f) => [
    { ...f, __parentKey: parentKey },
    ...(f.InverseParentStepFields
      ? flattenFields(f.InverseParentStepFields, f.Key ?? parentKey)
      : []),
  ]);
}

export function getFieldFormName(
  field: IStepFields & { __parentKey?: string },
): string {
  // Diseases inside Families
  if (field?.Key === "Diseases" && field?.__parentKey === "Families") {
    return "Families.0.Diseases";
  }

  return field?.Key ?? `Attributes.${field?.Id}`;
}

// ===================================
export function splitFieldsByLocation(fields: IStepFields[]) {
  return fields?.reduce(
    (acc, field) => {
      field?.Key ? acc?.root?.push(field) : acc?.attributes?.push(field);
      return acc;
    },
    { root: [] as IStepFields[], attributes: [] as IStepFields[] },
  );
}

export const cleanEmptyStrings = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === "string") {
    return obj.trim() === "" ? null : obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => cleanEmptyStrings(item));
  }

  if (typeof obj === "object") {
    const cleaned: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cleaned[key] = cleanEmptyStrings(obj[key]);
      }
    }
    return cleaned;
  }

  return obj;
};

type FieldMap = {
  keyName: string;
  attrName: string;
};

export const collectStepFields = (step: IStep): IStepFields[] => {
  const ownFields = flattenFields(step.StepFields ?? []);

  const childFields = step.InverseParentSteps?.flatMap(collectStepFields) ?? [];

  return [...ownFields, ...childFields];
};

export function getFieldsHasKeys(steps: any): FieldMap[] {
  const fields = steps.flatMap(collectStepFields);

  return fields
    .filter((f: any) => f.Key)
    .map((f: any) => ({
      keyName: f.Key,
      attrName: `${f.Id}`,
    }));
}


export const filterByVisibleKeys = (
  values: Record<string, any>,
  visibleKeys: string[]
) => {
  const result: Record<string, any> = {};

  visibleKeys.forEach((key) => {
    const parts = key.split(".");

    if (parts.length === 1) {
      if (values[key] !== undefined) {
        result[key] = values[key];
      }
      return;
    }

    // Handle nested keys like Attributes.35
    const [parent, child] = parts;

    if (values[parent] !== undefined) {
      if (!result[parent]) {
        result[parent] = Array.isArray(values[parent]) ? [] : {};
      }

      if (Array.isArray(values[parent])) {
        result[parent][Number(child)] = values[parent][Number(child)];
      } else {
        result[parent][child] = values[parent][child];
      }
    }
  });

  return result;
};

export const cleanEmptyValues = (obj: any): any => {
  if (Array.isArray(obj)) {
    const cleaned = obj
      .map(cleanEmptyValues)
      .filter((value) => value !== undefined);

    return cleaned.length > 0 ? cleaned : undefined;
  }

  if (obj && typeof obj === "object") {
    const cleaned = Object.fromEntries(
      Object.entries(obj)
        .map(([key, value]) => [key, cleanEmptyValues(value)])
        .filter(([_, value]) => value !== undefined)
    );

    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  if (
    obj === null ||
    obj === undefined ||
    (typeof obj === "string" && obj.trim() === "")
  ) {
    return undefined;
  }

  return obj;
};


export const getPrevVisibleSubStepIndex = (subSteps:any, currentIndex:number) => {
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (subSteps[i]?.IsVisible) return i;
  }
  return -1;
};


export const getFirstVisibleSubStepIndex = (subSteps = []) => {
  const idx = subSteps?.findIndex((s:any) => s?.IsVisible);
  return idx === -1 ? 0 : idx;
};

export const getNextVisibleSubStepIndex = (subSteps:any, currentIndex:number) => {
  for (let i = currentIndex + 1; i < subSteps.length; i++) {
    if (subSteps[i]?.IsVisible) return i;
  }
  return -1;
};

